import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudQueue as ApiIcon,
  Psychology as AiIcon,
  Description as DocIcon,
  Settings as GeneralIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import apiClient from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/home/Navbar";

interface ConfigItem {
  config_key: string;
  config_value: string | null;
  config_type: string;
  description: string;
  category: string;
  is_sensitive: boolean;
  is_public: boolean;
  is_predefined: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});

  const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.role === "zhaoyuan_admin";

  // 加载配置
  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.systemConfig.getAll();
      if (response.code === 200) {
        setConfigs(response.data);

        // 初始化编辑值
        const initialValues: Record<string, string> = {};
        response.data.forEach((config: ConfigItem) => {
          if (config.config_value && config.config_value !== "******") {
            initialValues[config.config_key] = config.config_value;
          } else {
            initialValues[config.config_key] = "";
          }
        });
        setEditedValues(initialValues);
      }
    } catch (error: any) {
      console.error("加载配置失败:", error);
      setMessage({ type: "error", text: "加载配置失败: " + (error.response?.data?.message || error.message) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  // 保存配置
  const handleSave = async (category: string) => {
    try {
      setSaving(true);
      setMessage(null);

      // 收集当前分类的配置
      const categoryConfigs: Record<string, string> = {};
      configs
        .filter((config) => config.category === category)
        .forEach((config) => {
          const value = editedValues[config.config_key];
          if (value !== undefined && value !== "") {
            categoryConfigs[config.config_key] = value;
          }
        });

      const response = await apiClient.systemConfig.update(categoryConfigs);
      if (response.code === 200) {
        setMessage({ type: "success", text: "配置保存成功" });
        await loadConfigs(); // 重新加载配置
      }
    } catch (error: any) {
      console.error("保存配置失败:", error);
      setMessage({ type: "error", text: "保存配置失败: " + (error.response?.data?.message || error.message) });
    } finally {
      setSaving(false);
    }
  };

  // 切换密码可见性
  const togglePasswordVisibility = (key: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 处理输入变化
  const handleInputChange = (key: string, value: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 渲染配置项
  const renderConfigItem = (config: ConfigItem) => {
    const isPassword = config.is_sensitive && !visiblePasswords[config.config_key];
    const displayValue = config.is_sensitive
      ? (editedValues[config.config_key] || "")
      : (editedValues[config.config_key] || config.config_value || "");

    return (
      <Grid item xs={12} key={config.config_key}>
        <TextField
          fullWidth
          label={config.description || config.config_key}
          value={displayValue}
          onChange={(e) => handleInputChange(config.config_key, e.target.value)}
          disabled={!isAdmin}
          type={config.is_sensitive && !visiblePasswords[config.config_key] ? "password" : "text"}
          multiline={config.config_type === "string" && !config.is_sensitive}
          rows={config.config_type === "string" && !config.is_sensitive ? 2 : 1}
          helperText={config.is_sensitive ? "敏感信息，请谨慎修改" : undefined}
          InputProps={{
            endAdornment: config.is_sensitive ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => togglePasswordVisibility(config.config_key)}
                  edge="end"
                >
                  {visiblePasswords[config.config_key] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: isAdmin ? "inherit" : "rgba(0, 0, 0, 0.6)",
            },
          }}
        />
      </Grid>
    );
  };

  // 渲染配置分组
  const renderCategory = (category: string, title: string, icon: React.ReactNode) => {
    const categoryConfigs = configs.filter((config) => config.category === category);

    if (categoryConfigs.length === 0) return null;

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            {icon}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {title}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {categoryConfigs.map(renderConfigItem)}
          </Grid>
          {isAdmin && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => handleSave(category)}
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : "保存配置"}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 4, mt: 8 }}>
        {/* 头部：返回按钮和标题 */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
            title="返回"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            系统设置
          </Typography>
        </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          只有管理员可以修改系统配置。您当前以只读模式查看。
        </Alert>
      )}

      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="API 配置" />
          <Tab label="AI 模型配置" />
          <Tab label="通用配置" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderCategory("api", "API 服务配置", <ApiIcon color="primary" />)}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderCategory("ai", "AI 模型配置", <AiIcon color="secondary" />)}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {renderCategory("general", "通用配置", <GeneralIcon color="action" />)}
        {renderCategory("document", "文档处理配置", <DocIcon color="info" />)}
      </TabPanel>
      </Container>
    </>
  );
};

export default SettingsPage;
